import React, { useState, useEffect, useCallback, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import { CurrencyDollar, ChartLineUp, Target, Lightning, Plug, Funnel, SquaresFour, ChartBar } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import Sidebar from "@/components/Sidebar";
import CampaignCard from "@/components/CampaignCard";
import CampaignDetailSheet from "@/components/CampaignDetailSheet";
import PlatformComparison from "@/components/PlatformComparison";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import { useWebSocket } from "@/hooks/useWebSocket";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency, formatRoas, formatNumber } from "@/lib/format";
import { requestNotificationPermission, showBrowserNotification } from "@/lib/pushNotifications";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [activePlatform, setActivePlatform] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("updated_at");
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("campaigns");

  const fetchAll = useCallback(async () => {
    const [accRes, campRes, sumRes] = await Promise.all([
      api.get("/integrations/accounts"),
      api.get("/campaigns"),
      api.get("/campaigns/summary"),
    ]);
    setAccounts(accRes.data);
    setCampaigns(campRes.data);
    setSummary(sumRes.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
    requestNotificationPermission();
  }, [fetchAll]);

  const handleWsMessage = useCallback((msg) => {
    if (msg.event === "campaign_update") {
      setCampaigns((prev) => {
        const map = new Map(prev.map((c) => [c.id, c]));
        msg.data.forEach((updated) => map.set(updated.id, { ...map.get(updated.id), ...updated }));
        return Array.from(map.values());
      });
    } else if (msg.event === "notification") {
      const n = msg.data;
      toast[n.type === "error" ? "error" : n.type === "warning" ? "warning" : n.type === "success" ? "success" : "info"](n.title, {
        description: n.message,
      });
      showBrowserNotification(n.title, n.message, n.type);
    }
  }, []);

  useWebSocket(!!user, handleWsMessage);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const sumRes = await api.get("/campaigns/summary");
        setSummary(sumRes.data);
      } catch (e) {
        console.error("Failed to refresh campaign summary:", e);
      }
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const handleConnect = async (platform) => {
    try {
      const res = await api.get(`/integrations/${platform}-ads/connect`);
      if (res.data.mock) {
        await api.post(`/integrations/${platform}-ads/mock-connect`);
        toast.success(`${platform === "google" ? "Google" : "Meta"} Ads account connected (demo data)`);
        fetchAll();
      } else {
        window.top.location.href = res.data.auth_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to connect account");
    }
  };

  const filteredCampaigns = useMemo(() => {
    let result = [...campaigns];
    if (activePlatform) result = result.filter((c) => c.platform === activePlatform);
    if (statusFilter !== "all") result = result.filter((c) => c.status === statusFilter);
    result.sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      return (b[sortBy] ?? 0) - (a[sortBy] ?? 0);
    });
    return result;
  }, [campaigns, activePlatform, statusFilter, sortBy]);

  const openCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setSheetOpen(true);
  };

  const handleCampaignUpdated = (updated) => {
    setCampaigns((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setSelectedCampaign(updated);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar onToggleSidebar={() => setMobileSidebarOpen(true)} />
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
        accounts={accounts}
        activePlatform={activePlatform}
        onSelectPlatform={setActivePlatform}
        mobileOpen={mobileSidebarOpen}
        onCloseMobile={() => setMobileSidebarOpen(false)}
      />

      <main className={`pt-16 transition-all duration-200 ${sidebarCollapsed ? "lg:pl-16" : "lg:pl-64"}`}>
        <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div>
              <h1 className="font-heading font-semibold text-2xl tracking-tight">Dashboard</h1>
              <p className="text-sm text-muted-foreground mt-1">Live view across all connected ad accounts</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => handleConnect("google")} data-testid="connect-google-ads-button">
                <Plug className="h-4 w-4 mr-2" /> Connect Google Ads
              </Button>
              <Button variant="outline" onClick={() => handleConnect("meta")} data-testid="connect-meta-ads-button">
                <Plug className="h-4 w-4 mr-2" /> Connect Meta Ads
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <SummaryCard icon={CurrencyDollar} label="Total Spend" value={formatCurrency(summary?.total_spend)} testId="summary-total-spend" />
            <SummaryCard icon={Target} label="Conversions" value={formatNumber(summary?.total_conversions)} testId="summary-conversions" />
            <SummaryCard icon={Lightning} label="Live Campaigns" value={`${summary?.live_campaigns ?? 0}/${summary?.total_campaigns ?? 0}`} testId="summary-live-campaigns" />
            <SummaryCard icon={ChartLineUp} label="Avg ROAS" value={formatRoas(summary?.avg_roas)} testId="summary-avg-roas" />
          </div>

          <div className="mb-5">
            <Tabs value={viewMode} onValueChange={setViewMode}>
              <TabsList data-testid="dashboard-view-tabs">
                <TabsTrigger value="campaigns" data-testid="view-tab-campaigns">
                  <SquaresFour className="h-4 w-4 mr-1.5" /> Campaigns
                </TabsTrigger>
                <TabsTrigger value="compare" data-testid="view-tab-compare">
                  <ChartBar className="h-4 w-4 mr-1.5" /> Compare Platforms
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {viewMode === "compare" ? (
            <PlatformComparison />
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Funnel className="h-4 w-4" /> Filter:
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40" data-testid="filter-status-select"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="live">Live</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-48" data-testid="sort-by-select"><SelectValue placeholder="Sort by" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated_at">Recently Updated</SelectItem>
                    <SelectItem value="spend">Spend (High to Low)</SelectItem>
                    <SelectItem value="roas">ROAS (High to Low)</SelectItem>
                    <SelectItem value="ctr">CTR (High to Low)</SelectItem>
                    <SelectItem value="name">Name (A-Z)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {!loading && filteredCampaigns.length === 0 && (
                <Card className="p-12 text-center" data-testid="empty-campaigns-state">
                  <Plug className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <h3 className="font-heading font-medium text-lg mb-1">No campaigns yet</h3>
                  <p className="text-sm text-muted-foreground mb-5">Connect a Google Ads or Meta Ads account to see live campaign data here.</p>
                  <div className="flex items-center justify-center gap-2">
                    <Button onClick={() => handleConnect("google")}>Connect Google Ads</Button>
                    <Button variant="outline" onClick={() => handleConnect("meta")}>Connect Meta Ads</Button>
                  </div>
                </Card>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                <AnimatePresence>
                  {filteredCampaigns.map((campaign) => (
                    <CampaignCard key={campaign.id} campaign={campaign} onClick={openCampaign} />
                  ))}
                </AnimatePresence>
              </div>
            </>
          )}
        </div>
      </main>

      <CampaignDetailSheet
        campaign={selectedCampaign}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onUpdated={handleCampaignUpdated}
      />
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, testId }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className="h-7 w-7 rounded-md bg-primary/10 flex items-center justify-center">
          <Icon className="h-3.5 w-3.5 text-primary" weight="bold" />
        </div>
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-xl sm:text-2xl font-heading font-semibold tabular-nums">{value}</p>
    </Card>
  );
}
